import { Backdrop, Checkbox, makeStyles, Modal } from '@material-ui/core'
import {
    CompositeFilterDescriptor,
    filterBy,
    orderBy,
    SortDescriptor,
} from '@progress/kendo-data-query'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import {
    Grid,
    // GridCellProps,
    GridColumn,
    // GridRowClickEvent,
    GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { formatDate } from '@telerik/kendo-intl'
import React, { ChangeEvent, useEffect, useState } from 'react' //, useCallback
import AWCLogo from '../Images/AWCLogo.png'
import { CustomerInfo, FilterCheckBox, FilterCustomerCheckBox } from './ShopMonitorFilterModal'
import './ShopMonitor.scss'
import { getAPIURL, getBaseURL } from '../../GlobalComponents/URLs'
import _, { debounce } from 'lodash'
import useWindowDimensions, { addYears, getCookie } from '../../GlobalComponents/GlobalFunctions'
import {
    AssetCategory,
    AssetType,
    fetchAssetCategories,
    fetchAssetTypes,
    fetchServiceTypes,
    ServiceType,
} from '../../features/forms/formsAPI'
import DashboardMultiSelect from '../../GlobalComponents/_misc/DashboardMultiSelect'
import '../../GlobalComponents/_misc/DashboardMultiSelect.css'
import {
    fqAlert,
    NotificationManager,
} from '../../GlobalComponents/NotificationManager/NotificationManager'

const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        outline: 0,
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        // border: '2px solid #000',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
    },
}))

const renderSwitch = (serviceType: string) => {
    switch (serviceType) {
        case 'A':
            return <button className='pillButtonGrey'>NEW ASSEMBLY</button>
        case 'B':
            return <button className='pillButtonDefault'>BENCHMARK SCAN</button>
        case 'C':
            return <button className='pillButtonDefault'>CONTROL LOOP CHECK</button>
        case 'F':
            return <button className='pillButtonDefault'>FIELD SERVICE</button>
        case 'I':
            return <button className='pillButtonDefault'>INSPECTION</button>
        case 'L':
            return <button className='pillButtonDefault'>VALVE LEAK TEST</button>
        case 'M':
            return <button className='pillButtonDefault'>REMANUFACTURED</button>
        case 'N':
            return <button className='pillButtonDefault'>NO WORK</button>
        case 'O':
            return <button className='pillButtonDefault'>OTHER</button>
        case 'R':
            return <button className='pillButtonGreen'>REPAIR</button>
        case 'S':
            return <button className='pillButtonDefault'>FIELD SURVEY</button>
        case 'T':
            return <button className='pillButtonGreen'>TEST ONLY</button>
        default:
            return <button className='pillButtonDefault'>{serviceType}</button>
    }
}

const renderServiceTypeFilter = (serviceType: string) => {
    switch (serviceType) {
        case 'A':
            return 'New Assembly'
        case 'B':
            return 'Benchmark Scan'
        case 'C':
            return 'Control Loop Check'
        case 'F':
            return 'Field Service'
        case 'I':
            return 'Inspection'
        case 'L':
            return 'Valve Leak Test'
        case 'M':
            return 'Remanufactured'
        case 'N':
            return 'No Work'
        case 'O':
            return 'Other'
        case 'R':
            return 'Repair'
        case 'S':
            return 'Field Survey'
        case 'T':
            return 'Test Only'
        default:
            return serviceType
    }
}

const renderReversedServiceType = (serviceType: string) => {
    switch (serviceType) {
        case 'New Assembly':
            return 'A'
        case 'Benchmark Scan':
            return 'B'
        case 'Control Loop Check':
            return 'C'
        case 'Field Service':
            return 'F'
        case 'Valve Leak Test':
            return 'L'
        case 'Inspection':
            return 'I'
        case 'Remanufactured':
            return 'M'
        case 'No Work':
            return 'N'
        case 'Other':
            return 'O'
        case 'Repair':
            return 'R'
        case 'Field Survey':
            return 'S'
        case 'Test Only':
            return 'T'
        default:
            return serviceType
    }
}

const convertServiceTypeList = (list: string[]) => {
    const tempList = []
    for (let x in list) {
        tempList.push(renderReversedServiceType(list[x]))
    }

    return tempList
}

export const ShopMonitor = () => {
    // Window screen dimensions
    const { height } = useWindowDimensions()
    const classes = useStyles()
    document.title = 'Shop Log'

    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)

    // AWC warehouse cookie and company code
    // document.cookie = "AWCWhs=03";
    let warehouseParam = urlParams.get('wh')
    let companyCode = urlParams.get('co')
    const isWarehouseShopInURL: boolean = warehouseParam ? true : false
    const isWarehouseCoRootInURL: boolean = companyCode ? true : false

    if (!warehouseParam) {
        warehouseParam = getCookie('AWCWhs')
    }

    const serviceTypeCookie = getCookie('ServiceType')
    const assetTypeCookie = getCookie('AssetType')

    // if (!companyCode) {
    //   companyCode = getCookie("CoRoot");
    // }

    // Warehouse data
    const [warehouseList, setWarehouseList] = useState<any>()
    const [warehouseNumber, setWarehouseNumber] = useState<string>()
    const [warehouseListItem, setWarehouseListItem] = useState<string>()

    // Shop log data for the grid
    const [shopLogData, setShopLogData] = useState<any>()

    // Show completed/quotes
    const [showCompleted, setShowCompleted] = useState<boolean>(false)
    const [showQuotes, setShowQuotes] = useState<boolean>(false)

    // Shop monitor refresh timer
    const [timerRefresh, setTimerRefresh] = useState<Date>(new Date())

    // Popup window timer
    const [windowTimerRefresh, setWindowTimerRefresh] = useState<Date>(new Date())

    // Initial state for filter and sorting
    const initialFilter: CompositeFilterDescriptor = {
        logic: 'and',
        filters: [],
    }
    const [filter, setFilter] = useState(initialFilter)
    const [textInputFilters, setTextInputFilters] = useState([])
    const [modalFilters, setModalFilters] = useState([
        { field: 'isQuote', operator: 'eq', value: showQuotes },
    ])
    const [activeViewFilterButton, setActiveViewFilterButton] = useState<boolean>(false)

    const initialSort: Array<SortDescriptor> = [
        { field: 'PreSortKey', dir: 'desc' },
        { field: 'IsRush', dir: 'desc' },
        { field: 'RequiredShipDate', dir: 'asc' },
        { field: 'OrderNumber', dir: 'asc' },
        { field: 'OrderDetailID', dir: 'asc' },
        { field: 'OrderTagID', dir: 'asc' },
    ]
    const [sort, setSort] = React.useState(initialSort)

    // Filter data
    const [salesOrderNumberFilter, setSalesOrderNumberFilter] = useState<string>('')
    const [tagNumberFilter, setTagNumberFilter] = useState<string>('')
    const [serialNumberFilter, setSerialNumberFilter] = useState<string>('')

    const onChangeSONumber = (event: any) => {
        debouncedSearchSONumber(event.target.value)
    }

    const debouncedSearchSONumber = debounce(function (query) {
        setSalesOrderNumberFilter(query)
    }, 500)

    const onChangeTagNumber = (event: any) => {
        debouncedSearchTagNumber(event.target.value)
    }

    const debouncedSearchTagNumber = debounce(function (query) {
        setTagNumberFilter(query)
    }, 500)

    const onChangeSerialNumber = (event: any) => {
        debouncedSearchSerialNumber(event.target.value)
    }

    const debouncedSearchSerialNumber = debounce(function (query) {
        setSerialNumberFilter(query)
    }, 500)

    // Filter modal data
    // Customer filter
    const [customerFilterList, setCustomerFilterList] = useState<CustomerInfo[]>([])
    const [selectedCustomerFilterList, setSelectedCustomerFilterList] = useState<CustomerInfo[]>([])
    const handleCustomerFilterSelect = (list: CustomerInfo[]) => {
        if (list.length > 0) {
            setResetable(true)
        }
        setAppliable(true)
        setSelectedCustomerFilterList(list)
    }

    // Order number filter
    const [orderNumberFilterList, setOrderNumberFilterList] = useState<string[]>([])
    const [selectedOrderNumberFilterList, setSelectedOrderNumberFilterList] = useState<string[]>([])
    const handleOrderNumberFilterSelect = (list: string[]) => {
        if (list.length > 0) {
            setResetable(true)
        }
        setAppliable(true)
        setSelectedOrderNumberFilterList(list)
    }

    // Stage filter
    const [stageFilterList, setStageFilterList] = useState<string[]>([])
    const [selectedStageFilterList, setSelectedStageFilterList] = useState<string[]>([])
    const handleStageFilterSelect = (list: string[]) => {
        if (list.length > 0) {
            setResetable(true)
        }
        setAppliable(true)
        setSelectedStageFilterList(list)
    }

    // Tech filter
    const [techFilterList, setTechFilterList] = useState<string[]>([])
    const [selectedTechFilterList, setSelectedTechFilterList] = useState<string[]>([])
    const handleTechFilterSelect = (list: string[]) => {
        if (list.length > 0) {
            setResetable(true)
        }
        setAppliable(true)
        setSelectedTechFilterList(list)
    }

    // Service type filter
    const [serviceTypeFilterList, setServiceTypeFilterList] = useState<string[]>([])
    const [selectedServiceTypeFilterList, setSelectedServiceTypeFilterList] = useState<string[]>([])
    const handleServiceTypeFilterSelect = (list: string[]) => {
        if (list.length > 0) {
            setResetable(true)
        }
        setAppliable(true)
        setSelectedServiceTypeFilterList(list)
    }

    // IAM filter
    const [IAMFilterList, setIAMFilterList] = useState<string[]>([])
    const [selectedIAMFilterList, setSelectedIAMFilterList] = useState<string[]>([])
    const handleIAMFilterSelect = (list: string[]) => {
        if (list.length > 0) {
            setResetable(true)
        }
        setAppliable(true)
        setSelectedIAMFilterList(list)
    }

    // Multi-select dropdown (not part of the filter modal)
    // Service type full list
    const [fullServiceTypeList, setFullServiceTypeList] = useState<ServiceType[]>([])
    const [selectedServiceTypeDefaultList, setSelectedServiceTypeDefaultList] = useState<
        ServiceType[]
    >([])
    const handleServiceTypeDefaultChange = (values: ServiceType[]) => {
        setSelectedServiceTypeDefaultList(values)
    }

    // Asset type filter (multi-select, not a part of the filter modal)
    const [fullAssetTypeList, setFullAssetTypeList] = useState<AssetType[]>([])
    const [selectedAssetTypeDefaultList, setSelectedAssetTypeDefaultList] = useState<any[]>([])
    const handleAssetTypeDefaultChange = (values: AssetType[]) => {
        setSelectedAssetTypeDefaultList(values)
    }

    // Modal toggle (open/close modal)
    const [filterModalOpen, setFilterModalOpen] = useState<boolean>(false)
    const [applyClicked, setApplyClicked] = useState<boolean>(false)
    const [resetable, setResetable] = useState<boolean>(false)
    const [appliable, setAppliable] = useState<boolean>(false)

    const openModal = () => {
        setFilterModalOpen(true)
    }

    const closeModal = (apply: boolean) => {
        setFilterModalOpen(false)
        setApplyClicked(apply)
    }

    // Fetch shop log data
    async function fetchShopLogData(showComp: boolean) {
        let shoplog_url = getAPIURL() + 'lcm/GetShopLogData'

        if (warehouseNumber) {
            await fetch(shoplog_url, {
                credentials: 'include',
                method: 'POST',
                headers: { 'Content-type': 'application/json' },
                body: JSON.stringify({
                    CompanyCode: companyCode ?? getCookie('CoRoot'),
                    Warehouse: warehouseNumber,
                    ShowCompleted: showComp,
                }),
            })
                .then((resp) => {
                    if (resp.ok) {
                        return resp.json()
                    } else {
                        // throw new Error('Bad request: ' + resp.statusText)
                        window.location.href =
                            getBaseURL() + 'masterclient/main.aspx?redirect=/lcmforms/shopmonitor'
                    }
                })
                .then((json) => {
                    if (json && json.length > 0) {
                        const tempData = json
                        // Adding PreSortKey to data
                        for (let dataIndex in tempData) {
                            // Presort key
                            if (!tempData[dataIndex].RequiredShipDate) {
                                tempData[dataIndex].PreSortKey = 1
                            } else {
                                tempData[dataIndex].PreSortKey = tempData[
                                    dataIndex
                                ].Stage.toUpperCase().startsWith('COMPLETED')
                                    ? 0
                                    : 2
                            }
                        }

                        // CanWorkQty
                        for (let dataIndex in tempData) {
                            if (tempData[dataIndex].isQuote) {
                                tempData[dataIndex].CanWorkQty = ''
                            } else {
                                if (tempData[dataIndex].CanWorkQty === -1) {
                                    tempData[dataIndex].CanWorkQty = 'Partial'
                                }
                            }
                        }

                        // Customer filter list
                        for (let dataIndex in tempData) {
                            const item: CustomerInfo = {
                                ShipToName: tempData[dataIndex].ShipToName,
                                ShipToCity: tempData[dataIndex].ShipToCity,
                                CustomerAccountCode: tempData[dataIndex].CustomerAccountCode,
                            }
                            if (item) {
                                if (
                                    !customerFilterList.find(
                                        (o: CustomerInfo) =>
                                            o.ShipToName === item.ShipToName &&
                                            o.ShipToCity === item.ShipToCity &&
                                            o.CustomerAccountCode === item.CustomerAccountCode
                                    )
                                ) {
                                    if (item.ShipToName) {
                                        customerFilterList.push(item)
                                    }
                                }
                            }
                        }

                        // Order number filter list
                        for (let dataIndex in tempData) {
                            const item = tempData[dataIndex].OrderNumber
                            if (item) {
                                if (orderNumberFilterList.indexOf(item) === -1) {
                                    orderNumberFilterList.push(item)
                                }
                            }
                        }

                        // Stage filter list
                        for (let dataIndex in tempData) {
                            const item = tempData[dataIndex].Stage

                            if (item) {
                                if (stageFilterList.indexOf(item) === -1) {
                                    stageFilterList.push(item)
                                }
                            }
                        }

                        // Tech filter list
                        for (let dataIndex in tempData) {
                            const item = tempData[dataIndex].TechUsername

                            if (item) {
                                if (techFilterList.indexOf(item) === -1) {
                                    techFilterList.push(item)
                                }
                            }
                        }

                        // Service type
                        for (let dataIndex in tempData) {
                            const item = renderServiceTypeFilter(tempData[dataIndex].ServiceType)
                            if (item) {
                                if (serviceTypeFilterList.indexOf(item) === -1) {
                                    serviceTypeFilterList.push(item)
                                }
                            }
                        }

                        // IAM
                        for (let dataIndex in tempData) {
                            const item = tempData[dataIndex].IAMUsername
                            if (item) {
                                if (IAMFilterList.indexOf(item) === -1) {
                                    IAMFilterList.push(item)
                                }
                            }
                        }
                        setShopLogData(tempData)
                    } else {
                        setShopLogData([])
                    }
                })
                .finally(() => {
                    setLoading(false)
                })
        }
    }

    // Get service types
    async function getServiceTypes() {
        const tempServiceTypeList = await fetchServiceTypes()

        const tempSelectedServiceList: ServiceType[] = []
        if (serviceTypeCookie) {
            const serviceTypeCookieSplitList: string[] = serviceTypeCookie.split('|')

            tempServiceTypeList.forEach((service: ServiceType) => {
                if (serviceTypeCookieSplitList.includes(service.ServiceTypeID)) {
                    tempSelectedServiceList.push(service)
                }
            })
        }

        setSelectedServiceTypeDefaultList(tempSelectedServiceList)
        setFullServiceTypeList(tempServiceTypeList)
    }

    // Get asset types
    async function getAssetTypes() {
        const categories = await fetchAssetCategories()
        const tempFullAssetList: AssetType[] = []
        const tempSelectedAssetList: AssetType[] = []
        let assetTypeCookieSplitList: string[] = []

        if (assetTypeCookie) {
            assetTypeCookieSplitList = assetTypeCookie.split('|')
        }

        let resp = await Promise.all(
            categories.map(async (category: AssetCategory) => {
                const assetTypes = await fetchAssetTypes(category.ID)
                return { assetTypes }
            })
        )

        resp.forEach((value) => {
            value.assetTypes.forEach((value: AssetType) => {
                tempFullAssetList.push(value)
                if (assetTypeCookie && assetTypeCookieSplitList.includes(value.ID.toString())) {
                    tempSelectedAssetList.push(value)
                }
            })
        })

        setSelectedAssetTypeDefaultList(tempSelectedAssetList)
        setFullAssetTypeList(tempFullAssetList)
    }

    // Get both of service types and asset types on laod
    useEffect(() => {
        getServiceTypes()
        getAssetTypes()
    }, [])

    // If selected service type changes, then need to change it in the filter modal
    useEffect(() => {
        const tempSelectedList: string[] = []

        selectedServiceTypeDefaultList.forEach((serviceType: ServiceType) => {
            tempSelectedList.push(serviceType.ServiceDescription)
        })

        setResetable(true)

        setSelectedServiceTypeFilterList(tempSelectedList)
    }, [selectedServiceTypeDefaultList, warehouseNumber])

    // Filter inputs (on main page)
    useEffect(() => {
        let tempFilter: any = []
        if (salesOrderNumberFilter) {
            const obj = {
                field: 'OrderNumber',
                operator: 'contains',
                value: `${salesOrderNumberFilter}`,
            }
            tempFilter.push(obj)
        }
        if (tagNumberFilter) {
            const obj = {
                field: 'Tag',
                operator: 'contains',
                value: `${tagNumberFilter}`,
            }

            tempFilter.push(obj)
        }
        if (serialNumberFilter) {
            const obj = {
                field: 'SerialNumber',
                operator: 'contains',
                value: `${serialNumberFilter}`,
            }
            tempFilter.push(obj)
        }

        setTextInputFilters(tempFilter)
    }, [salesOrderNumberFilter, tagNumberFilter, serialNumberFilter])

    // Filter modal toggles
    useEffect(() => {
        if (filterModalOpen) {
            // Open modal
        } else {
            // Modal closed with apply button
            // if (applyClicked) {
            let tempFilter: any = []

            // Show Quotes toggle
            const obj = {
                field: 'isQuote',
                operator: 'eq',
                value: showQuotes,
            }

            tempFilter.push(obj)

            // Show Completed toggle

            if (warehouseNumber) {
                setLoading(true)
                fetchShopLogData(showCompleted)
            }

            // Customer filter
            if (selectedCustomerFilterList.length > 0) {
                const customerTempFilter: any = []

                for (let dataIndex in selectedCustomerFilterList) {
                    const obj = {
                        logic: 'and',
                        filters: [
                            {
                                field: 'ShipToName',
                                operator: 'eq',
                                value: selectedCustomerFilterList[dataIndex].ShipToName,
                            },
                            {
                                field: 'ShipToCity',
                                operator: 'eq',
                                value: selectedCustomerFilterList[dataIndex].ShipToCity,
                            },
                            {
                                field: 'CustomerAccountCode',
                                operator: 'eq',
                                value: selectedCustomerFilterList[dataIndex].CustomerAccountCode,
                            },
                        ],
                    }

                    customerTempFilter.push(obj)
                }
                const customerObj = {
                    logic: 'or',
                    filters: customerTempFilter,
                }

                tempFilter.push(customerObj)
            }

            // Order number filter
            if (selectedOrderNumberFilterList.length > 0) {
                const orderNumberTempFilter: any = []

                for (let dataIndex in selectedOrderNumberFilterList) {
                    const obj = {
                        field: 'OrderNumber',
                        operator: 'eq',
                        value: selectedOrderNumberFilterList[dataIndex],
                    }

                    orderNumberTempFilter.push(obj)
                }

                const orderNumberObj = {
                    logic: 'or',
                    filters: orderNumberTempFilter,
                }

                tempFilter.push(orderNumberObj)
            }

            // Stage filter
            if (selectedStageFilterList.length > 0) {
                const stageTempFilter: any = []

                for (let dataIndex in selectedStageFilterList) {
                    const obj = {
                        field: 'Stage',
                        operator: 'eq',
                        value: selectedStageFilterList[dataIndex],
                    }

                    stageTempFilter.push(obj)
                }

                const stageObj = {
                    logic: 'or',
                    filters: stageTempFilter,
                }

                tempFilter.push(stageObj)
            }

            // Tech filter
            if (selectedTechFilterList.length > 0) {
                const techTempFilter: any = []

                for (let dataIndex in selectedTechFilterList) {
                    const obj = {
                        field: 'TechUsername',
                        operator: 'eq',
                        value: selectedTechFilterList[dataIndex],
                    }

                    techTempFilter.push(obj)
                }

                const techObj = {
                    logic: 'or',
                    filters: techTempFilter,
                }

                tempFilter.push(techObj)
            }

            // Service type filter
            if (selectedServiceTypeFilterList.length > 0) {
                const serviceTypeTempFilter: any = []

                for (let dataIndex in selectedServiceTypeFilterList) {
                    const obj = {
                        field: 'ServiceType',
                        operator: 'eq',
                        value: renderReversedServiceType(selectedServiceTypeFilterList[dataIndex]),
                    }

                    serviceTypeTempFilter.push(obj)
                }

                const serviceTypeObj = {
                    logic: 'or',
                    filters: serviceTypeTempFilter,
                }

                tempFilter.push(serviceTypeObj)
            }

            // IAM filter
            if (selectedIAMFilterList.length > 0) {
                const IAMTempFilter: any = []

                for (let dataIndex in selectedIAMFilterList) {
                    const obj = {
                        field: 'IAMUsername',
                        operator: 'eq',
                        value: selectedIAMFilterList[dataIndex],
                    }

                    IAMTempFilter.push(obj)
                }

                const IAMObj = {
                    logic: 'or',
                    filters: IAMTempFilter,
                }

                tempFilter.push(IAMObj)
            }

            // Asset type filter
            if (selectedAssetTypeDefaultList.length > 0) {
                const assetTypeTempFilter: any = []

                for (let dataIndex in selectedAssetTypeDefaultList) {
                    const obj = {
                        field: 'CustomerAssetType',
                        operator: 'eq',
                        value: selectedAssetTypeDefaultList[dataIndex].ID,
                    }

                    assetTypeTempFilter.push(obj)
                }

                const assetTypeObj = {
                    logic: 'or',
                    filters: assetTypeTempFilter,
                }

                tempFilter.push(assetTypeObj)
            }

            setAppliable(false)
            setApplyClicked(false)
            setModalFilters(tempFilter)
            // } else {
            //     // Modal is current closed
            // }
        }
    }, [filterModalOpen, applyClicked, selectedServiceTypeFilterList, selectedAssetTypeDefaultList])

    // Control for all filters
    useEffect(() => {
        let tempFilter: any = []
        if (textInputFilters.length > 0) {
            tempFilter = textInputFilters
        }

        if (modalFilters.length > 0) {
            tempFilter = tempFilter.concat(modalFilters)
        }

        const defaultFilter = [{ field: 'isQuote', operator: 'eq', value: false }]

        if (
            JSON.stringify(tempFilter) === JSON.stringify(defaultFilter) &&
            showCompleted === false
        ) {
            setActiveViewFilterButton(false)
        } else {
            setActiveViewFilterButton(true)
        }

        setFilter({ logic: 'and', filters: tempFilter })
    }, [textInputFilters, modalFilters])

    // Get warehouse locations
    useEffect(() => {
        let warehouse_url = getAPIURL() + 'Warehouse/Warehouses'

        fetch(warehouse_url, {
            credentials: 'include',
            method: 'POST',
            headers: { 'Content-type': 'application/json' },
            body: JSON.stringify(
                companyCode
                    ? { coRoot: companyCode, warehouseType: 'SHOP' }
                    : { warehouseType: 'SHOP' }
            ),
        })
            .then((resp) => {
                if (resp.ok) {
                    return resp.json()
                } else throw new Error('Bad request: ' + resp.statusText)
            })
            .then((json) => {
                if (json && json.length > 0) {
                    let tempList = []
                    for (let num in json) {
                        tempList.push(json[num].locnum + '- ' + json[num].name)
                        if (warehouseParam!.trim() === json[num].locnum.trim()) {
                            setWarehouseNumber(warehouseParam!)
                        }
                    }
                    setWarehouseList(tempList)
                } else {
                    // Assuming this means not logged in
                    window.location.href =
                        getBaseURL() + 'masterclient/main.aspx?redirect=/lcmforms/shopmonitor'
                }
            })
    }, [warehouseParam])

    // Loading state for whenever the warehouse number changes
    const [loading, setLoading] = useState<boolean>(false)

    // Shop Log data
    useEffect(() => {
        if (warehouseNumber) {
            setLoading(true)
            fetchShopLogData(showCompleted)
        }
    }, [warehouseNumber])

    // Warehouse list default (based on warehouse cookie or param)
    useEffect(() => {
        if (warehouseList && warehouseParam) {
            for (let tempItem in warehouseList) {
                if (warehouseNumber) {
                    if (
                        warehouseNumber.trim() ===
                        warehouseList[tempItem].match(/(\d\d) (.*)/).slice(1)[0]
                    ) {
                        setWarehouseListItem(warehouseList[tempItem])
                    }
                }
            }
        }
    }, [warehouseList])

    // Reset all modal filters
    const resetModalFilters = () => {
        setResetable(false)
        setApplyClicked(false)
        setAppliable(false)
        setShowCompleted(false)
        setShowQuotes(false)
        setModalFilters([{ field: 'isQuote', operator: 'eq', value: false }])
        setSelectedCustomerFilterList([])
        setSelectedOrderNumberFilterList([])
        setSelectedStageFilterList([])
        setSelectedTechFilterList([])
        setSelectedServiceTypeFilterList([])
        setSelectedIAMFilterList([])
    }

    // Handling warehouse number change
    const handleWarehouseChange = (event: any) => {
        // Reset all filters
        resetModalFilters()

        setSalesOrderNumberFilter('')
        setTagNumberFilter('')
        setSerialNumberFilter('')

        setCustomerFilterList([])
        setOrderNumberFilterList([])
        setStageFilterList([])
        setTechFilterList([])
        setServiceTypeFilterList([])
        setIAMFilterList([])

        if (event.target.value === '-- Select --') {
            setWarehouseListItem('-- Select --')
            setWarehouseNumber('')
        } else {
            setWarehouseListItem(event.target.value)
            // splitting ["01", "- Warehouse Name"]
            setWarehouseNumber(event.target.value.match(/(\d\d) (.*)/).slice(1)[0])
        }
    }

    // setting width of tech instruction
    const autoWidth = 'auto'

    /* Grid cells for the grid system */
    const GridServiceCell = (props: any) => {
        const assetType = fullAssetTypeList.find((asset) => {
            if (asset.ID === props.dataItem.CustomerAssetType) {
                return asset.Name
            }
        })
        return (
            <td style={{ minWidth: 100 }}>
                {props.dataItem.IsSteamService === 1 ? (
                    <div>
                        <button className='pillButtonOrange'>STEAM APPS</button>
                    </div>
                ) : (
                    <div>{renderSwitch(props.dataItem.ServiceType)}</div>
                )}
                <div
                    style={{
                        display: 'flex',
                        fontSize: 10,
                        fontWeight: 700,
                        marginTop: 10,
                        justifyContent: 'center',
                    }}
                >
                    {assetType?.Name}
                </div>
            </td>
        )
    }

    const GridCustomerCell = (props: any) => {
        return (
            <td style={{ minWidth: 130 }}>
                <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 5 }}>
                    {props.dataItem.ShipToName}
                </div>
                <div style={{ fontWeight: 500, fontSize: 10 }}>{props.dataItem.ShipToCity}</div>
                <div style={{ fontWeight: 500, fontSize: 18 }}>
                    {props.dataItem.CustomerOrderNumber}
                </div>
            </td>
        )
    }

    // State for pop up windows
    const [popUpClosed, setPopUpClosed] = useState<boolean>(false)

    useEffect(() => {
        if (popUpClosed) {
            setWindowTimerRefresh(new Date())
        }
    }, [popUpClosed])

    function openWin(openUrl: string) {
        setPopUpClosed(false)
        const win = window.open(openUrl, 'popUpWindow', 'width=1100,height=810')

        const timer = setInterval(() => {
            if (win?.closed) {
                setPopUpClosed(true)
                clearInterval(timer)
            }
        }, 1000)
    }

    const GridOrderNumberCell = (props: any) => {
        const value = props.dataItem[props.field]
        const url = value
            ? `${getBaseURL()}OPForms/OrderTagEntry.aspx?FromShopLog=true&SONum=${value}`
            : props.dataItem.LineItemNumber
            ? `${getBaseURL()}OPForms/OrderTagEntry.aspx?FromShopLog=true&SONum=${
                  props.dataItem.OrderNumber
              }&LI=${props.dataItem.LineItemNumber}&TagID=${props.dataItem.OrderTagID}`
            : `${getBaseURL()}OPForms/OrderTagEntry.aspx?FromShopLog=true&SONum=${
                  props.dataItem.OrderNumber
              }&TagID=${props.dataItem.OrderTagID}`

        return (
            <td onClick={() => openWin(url)} style={{ cursor: 'pointer', minWidth: 70 }}>
                <div className='shopMonitorLink'>{props.dataItem.OrderNumber}</div>
                {props.dataItem.IAMUsername && (
                    <div style={{ fontSize: 16, fontWeight: 500, marginTop: 10 }}>
                        {props.dataItem.IAMUsername}
                    </div>
                )}
            </td>
        )
    }

    const GridTagAssemblyCell = (props: any) => {
        const url = props.dataItem.LineItemNumber
            ? `${getBaseURL()}OPForms/OrderTagEntry.aspx?FromShopLog=true&SONum=${
                  props.dataItem.OrderNumber
              }&LI=${props.dataItem.LineItemNumber}&TagID=${props.dataItem.OrderTagID}`
            : `${getBaseURL()}OPForms/OrderTagEntry.aspx?FromShopLog=true&SONum=${
                  props.dataItem.OrderNumber
              }&TagID=${props.dataItem.OrderTagID}`

        return (
            <td onClick={() => openWin(url)} style={{ cursor: 'pointer', minWidth: 170 }}>
                <div className='shopMonitorLink'>{props.dataItem.Tag}</div>
                {props.dataItem.LineItemNumber && (
                    <div
                        style={
                            props.dataItem.Tag
                                ? { fontSize: 16, fontWeight: 500, marginTop: 10 }
                                : { fontSize: 16, fontWeight: 500, marginTop: 33 }
                        }
                    >
                        {props.dataItem.LineItemNumber}
                    </div>
                )}
            </td>
        )
    }

    const GridDateCell = (props: any) => {
        const value = props.dataItem[props.field]
        const reqShipDate = value ? formatDate(new Date(value), 'MM/dd/yy') : ''

        const onDockDate = new Date(props.dataItem.OnDockDate)

        const todayDate = new Date()
        todayDate.setHours(0, 0, 0, 0)
        const onDockDateMinusTransitTime = new Date(
            onDockDate.getTime() - props.dataItem.TransitTime * 24 * 60 * 60 * 1000
        )

        const isPastDue = value ? new Date(value) < new Date(todayDate.getTime()) : false

        const dateDifference = props.dataItem.OnDockDate
            ? todayDate.getTime() - onDockDateMinusTransitTime.getTime()
            : null
        const dateDifferenceInDays =
            dateDifference !== null
                ? Math.floor(dateDifference / (1000 * 3600 * 24))
                : // - props.dataItem.TransitTime
                  -1

        return props.dataItem.isQuote ? (
            <td style={{ minWidth: 110 }}>
                <div
                    style={{
                        fontSize: 16,
                        fontWeight: 500,
                    }}
                >
                    Quote
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 16,
                        fontWeight: 500,
                    }}
                >
                    {props.dataItem.IsRush === true ? (
                        <div className='rushIcon'>RUSH</div>
                    ) : (
                        <div></div>
                    )}
                    <div></div>
                </div>
            </td>
        ) : (
            <td>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 16,
                        fontWeight: 500,
                    }}
                >
                    {reqShipDate}{' '}
                    {reqShipDate !== '' &&
                        dateDifferenceInDays > 0 &&
                        !props.dataItem.Stage.toUpperCase().startsWith('COMPLETED') && (
                            <div>({dateDifferenceInDays})</div>
                        )}
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 16,
                        fontWeight: 500,
                    }}
                >
                    {props.dataItem.IsRush === true ? (
                        <div className='rushIcon'>RUSH</div>
                    ) : (
                        <div></div>
                    )}
                    {value &&
                        isPastDue &&
                        !props.dataItem.Stage.toUpperCase().startsWith('COMPLETED') && (
                            <div>
                                <button className='pillButtonYellow'>PAST DUE</button>
                            </div>
                        )}
                </div>
            </td>
        )
    }

    const GridQtyCell = (props: any) => {
        return (
            <td style={{ minWidth: 80 }}>
                <div style={{ fontSize: 16, fontWeight: 500 }}>
                    {props.dataItem.BOQty} /{' '}
                    <span
                        style={props.dataItem.CanWorkQty === 'Partial' ? { fontWeight: 900 } : {}}
                    >
                        {props.dataItem.CanWorkQty}
                    </span>
                </div>
            </td>
        )
    }

    const TechInstructionCell = (props: any) => {
        return (
            <td>
                <div
                    style={{
                        overflow: 'auto',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        minWidth: 150,
                    }}
                >
                    <div style={{ fontSize: 14, fontWeight: 400 }}>
                        {props.dataItem.LineItemTechnicianInstructions}
                    </div>
                    <div style={{ fontWeight: 500, marginTop: 5 }}>
                        {props.dataItem.TagTechnicianInstructions && (
                            <div
                                style={{
                                    borderLeft: '4px solid black',
                                    paddingLeft: 10,
                                    fontStyle: 'italic',
                                    fontSize: 14,
                                    fontWeight: 600,
                                }}
                            >
                                {props.dataItem.TagTechnicianInstructions}
                            </div>
                        )}
                    </div>
                </div>
            </td>
        )
    }

    const TechCell = (props: any) => {
        return (
            <td style={{ minWidth: 100 }}>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{props.dataItem.TechUsername}</div>
            </td>
        )
    }

    const StageCell = (props: any) => {
        const value = props.dataItem[props.field]
        return (
            <td style={{ minWidth: 100 }}>
                <div
                    style={{
                        fontSize: 16,
                        fontWeight: 500,
                    }}
                >
                    {value.replaceAll('/', ' / ')}
                </div>
            </td>
        )
    }

    const SerialNumberCell = (props: any) => {
        const value = props.dataItem[props.field]
        return (
            <td style={{ minWidth: 90 }}>
                <div
                    style={{
                        fontSize: 16,
                        fontWeight: 500,
                        wordBreak: 'break-all',
                    }}
                >
                    {value}
                </div>
            </td>
        )
    }

    useEffect(() => {
        if (isWarehouseShopInURL && isWarehouseCoRootInURL) {
            const interval = setInterval(() => {
                setTimerRefresh(new Date())
            }, 60000)
            return () => clearInterval(interval)
        }
    }, [])

    useEffect(() => {
        if (warehouseNumber) {
            fetchShopLogData(showCompleted)
        }
    }, [timerRefresh, windowTimerRefresh])

    return (
        <div className='lcmForms'>
            <div className='shopMonitor'>
                <div className='shopMonitorHeader'>
                    <div style={{ marginLeft: 25, marginTop: -5 }}>
                        <img
                            className='headerAWCLogo'
                            src={AWCLogo}
                            alt='Shop Monitor'
                            width={275}
                        />
                    </div>
                    <div style={{ fontWeight: 500 }}>
                        <DropDownList
                            className='inputContainer'
                            style={{
                                width: 434,
                                height: 32,
                            }}
                            data={warehouseList}
                            value={warehouseListItem}
                            defaultItem={'-- Select --'}
                            onChange={handleWarehouseChange}
                        />
                        <div className='shopMonitorFilters'>
                            {/* TagUID */}
                            <input
                                type='text'
                                placeholder='SO Number Filter'
                                defaultValue={salesOrderNumberFilter}
                                // onChange={(event: any) => {
                                //   setSalesOrderNumberFilter(event.target.value);
                                // }}
                                onChange={onChangeSONumber}
                                style={{ marginLeft: 1, width: 124 }}
                            />
                            {/* Tag */}
                            <input
                                id='TagNumberFilter'
                                type='text'
                                placeholder='Tag Number Filter'
                                defaultValue={tagNumberFilter}
                                // onChange={(event: any) => {
                                //   setTagNumberFilter(event.target.value);
                                // }}
                                onChange={onChangeTagNumber}
                                style={{ width: 125 }}
                            />
                            {/* SerialNumber */}
                            <input
                                id='SerialNumberFilter'
                                type='text'
                                placeholder='Serial Number Filter'
                                defaultValue={serialNumberFilter}
                                // onChange={(event: any) => {
                                //   setSerialNumberFilter(event.target.value);
                                // }}
                                onChange={onChangeSerialNumber}
                                style={{ width: 125 }}
                            />
                        </div>
                    </div>
                    <div className='shopMonitorFilterButtons'>
                        <div className='shopMonitorFilterButton'>
                            <button
                                className={
                                    activeViewFilterButton ? 'inverseCancelButton' : 'cancelButton'
                                }
                                style={{ width: 125 }}
                                onClick={() => {
                                    shopLogData ? openModal() : window.alert('Select a warehouse')
                                }}
                            >
                                View Filters
                            </button>
                        </div>
                        <div className='shopMonitorFilterButton'>
                            <button
                                className='cancelButton'
                                style={{ width: 125 }}
                                onClick={async () => {
                                    let export_url = getAPIURL() + 'lcm/GetShopLogDataReport'

                                    let tempAssetTypeFilter: number[] = []
                                    selectedAssetTypeDefaultList.forEach((assetType: AssetType) => {
                                        tempAssetTypeFilter.push(assetType.ID)
                                    })

                                    let req = await fetch(export_url, {
                                        credentials: 'include',
                                        method: 'POST',
                                        headers: { 'Content-type': 'application/json' },
                                        body: JSON.stringify({
                                            CompanyCode: companyCode,
                                            Warehouse: warehouseNumber,
                                            ShowCompleted: showCompleted,
                                            ShowQuotes: showQuotes,
                                            CustomerFilter: selectedCustomerFilterList,
                                            OrderFilter: selectedOrderNumberFilterList,
                                            StageFilter: selectedStageFilterList,
                                            TechFilter: selectedTechFilterList,
                                            IAMFilter: selectedIAMFilterList,
                                            ServiceTypeFilter: convertServiceTypeList(
                                                selectedServiceTypeFilterList
                                            ),
                                            AssetTypeFilter: tempAssetTypeFilter,
                                        }),
                                    })
                                    if (req.ok) {
                                        const blob = await req.blob()
                                        const newBlob = new Blob([blob])

                                        const blobUrl = window.URL.createObjectURL(newBlob)

                                        const link = document.createElement('a')
                                        link.href = blobUrl
                                        link.setAttribute('download', 'ShopLog.xlsx')
                                        document.body.appendChild(link)
                                        link.click()
                                        link.parentNode?.removeChild(link)

                                        // clean up Url
                                        window.URL.revokeObjectURL(blobUrl)
                                    }
                                }}
                            >
                                Export to Excel
                            </button>
                        </div>
                    </div>
                    <div className='shopMonitorDefaultSettings'>
                        <div style={{ width: '33%' }}>
                            <div>Service Type</div>
                            <div style={{ display: 'flex' }}>
                                <DashboardMultiSelect
                                    data={fullServiceTypeList}
                                    keyField={'ServiceTypeID'}
                                    textField={'ServiceDescription'}
                                    selectedValues={selectedServiceTypeDefaultList}
                                    onSelectedValuesChanged={handleServiceTypeDefaultChange}
                                    noArrow={true}
                                />
                            </div>
                        </div>
                        <div style={{ width: '33%' }}>
                            <div>Asset Type</div>
                            <div style={{ display: 'flex' }}>
                                <DashboardMultiSelect
                                    data={fullAssetTypeList}
                                    keyField={'ID'}
                                    textField={'Name'}
                                    selectedValues={selectedAssetTypeDefaultList}
                                    onSelectedValuesChanged={handleAssetTypeDefaultChange}
                                    noArrow={true}
                                />
                            </div>
                        </div>
                        <div style={{ width: '33%' }}>
                            <button
                                className='aButton'
                                style={{ width: 125, marginTop: 15 }}
                                onClick={() => {
                                    const tempServiceTypeList: string[] = []
                                    selectedServiceTypeDefaultList.forEach(
                                        (serviceType: ServiceType) => {
                                            tempServiceTypeList.push(serviceType.ServiceTypeID)
                                        }
                                    )
                                    const serviceTypeCookie = tempServiceTypeList.join('|')

                                    const tempAssetTypeList: string[] = []
                                    selectedAssetTypeDefaultList.forEach((assetType: AssetType) => {
                                        tempAssetTypeList.push(assetType.ID.toString())
                                    })
                                    const assetTypeCookie = tempAssetTypeList.join('|')

                                    const date = new Date()
                                    const dateInTenYears = addYears(date, 1)

                                    document.cookie = `ServiceType=${serviceTypeCookie};expires=${dateInTenYears}`
                                    document.cookie = `AssetType=${assetTypeCookie};expires=${dateInTenYears}`

                                    fqAlert({
                                        type: 'success',
                                        message: 'Default saved!',
                                    })
                                }}
                            >
                                Set as Default
                            </button>
                        </div>
                    </div>
                </div>

                <div className='shopMonitorGrid'>
                    {warehouseNumber &&
                        (loading ? (
                            <>
                                <div
                                    className={'k-i-loading'}
                                    style={{
                                        fontSize: 200,
                                        // position: "absolute",
                                        // left: "50%",
                                        // top: "50%",
                                        position: 'fixed',
                                        top: '50%',
                                        right: '50%',
                                    }}
                                ></div>
                                <NotificationManager key={'notificationsManager'} />
                            </>
                        ) : (
                            <>
                                {shopLogData && (
                                    <>
                                        <Grid
                                            data={orderBy(filterBy(shopLogData, filter), sort)}
                                            filter={filter}
                                            sortable={{ mode: 'multiple' }}
                                            sort={sort}
                                            scrollable='scrollable'
                                            style={{ height: height - 90 }}
                                        >
                                            <GridColumn
                                                title='Service / Asset'
                                                cell={GridServiceCell}
                                                width='140px'
                                            />
                                            <GridColumn
                                                title='Customer / PO #'
                                                cell={GridCustomerCell}
                                                width='160px'
                                            />
                                            <GridColumn
                                                title='Order # / IAM'
                                                cell={GridOrderNumberCell}
                                                field='OrderNumber'
                                                width='130px'
                                            />
                                            <GridColumn
                                                title='Tag / Assy LI #'
                                                cell={GridTagAssemblyCell}
                                                width='190px'
                                            />
                                            <GridColumn
                                                title='Req Ship Date'
                                                field='RequiredShipDate'
                                                cell={GridDateCell}
                                                width='135px'
                                            />
                                            <GridColumn
                                                title='Qty/Can Work'
                                                cell={GridQtyCell}
                                                width='140px'
                                            />

                                            <GridColumn
                                                title='Tech Instructions'
                                                cell={TechInstructionCell}
                                                width={autoWidth}
                                            />
                                            <GridColumn
                                                title='Stage'
                                                field='Stage'
                                                cell={StageCell}
                                                width='125px'
                                            />
                                            <GridColumn
                                                title='Tech'
                                                field='TechUsername'
                                                cell={TechCell}
                                                width='135px'
                                            />
                                            <GridColumn
                                                title='S/N'
                                                field='SerialNumber'
                                                cell={SerialNumberCell}
                                                width='120px'
                                            />
                                        </Grid>
                                        <NotificationManager key={'notificationsManager'} />
                                    </>
                                )}
                            </>
                        ))}
                </div>

                <Modal
                    open={filterModalOpen}
                    onClose={() => {
                        closeModal(false)
                    }}
                    className={classes.modal}
                    BackdropComponent={Backdrop}
                >
                    <div className='shopMonitorModalContainer'>
                        <div className='shopMonitorModal'>
                            <div
                                className='shopMonitorModalHeader'
                                style={{
                                    borderRadius: '4px 4px 0px 0px',
                                    padding: '17px 25px',
                                    fontSize: 18,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            >
                                {warehouseListItem} Filter
                                <span
                                    className='k-icon k-i-close'
                                    style={{ marginTop: 5, color: '#999999', cursor: 'pointer' }}
                                    onClick={() => {
                                        closeModal(false)
                                    }}
                                ></span>
                            </div>
                            <div className='shopMonitorModalBody'>
                                <div className='shopMonitorModalFilters'>
                                    <FilterCustomerCheckBox
                                        title={'Customer'}
                                        selected={selectedCustomerFilterList}
                                        handleSelectedChange={handleCustomerFilterSelect}
                                        checkboxList={customerFilterList.sort((a, b) => {
                                            if (a.ShipToName < b.ShipToName) {
                                                return -1
                                            }
                                            if (a.ShipToName > b.ShipToName) {
                                                return 1
                                            }
                                            return 0
                                        })}
                                        checkboxWidth={400}
                                    />
                                    <FilterCheckBox
                                        title={'Order #'}
                                        selected={selectedOrderNumberFilterList}
                                        handleSelectedChange={handleOrderNumberFilterSelect}
                                        checkboxList={orderNumberFilterList.sort()}
                                        checkboxWidth={125}
                                    />
                                    <FilterCheckBox
                                        title={'Stage'}
                                        selected={selectedStageFilterList}
                                        handleSelectedChange={handleStageFilterSelect}
                                        checkboxList={stageFilterList.sort()}
                                        checkboxWidth={250}
                                    />
                                    <FilterCheckBox
                                        title={'Tech'}
                                        selected={selectedTechFilterList}
                                        handleSelectedChange={handleTechFilterSelect}
                                        checkboxList={techFilterList.sort()}
                                        checkboxWidth={160}
                                    />
                                    <FilterCheckBox
                                        title={'Service Type'}
                                        selected={selectedServiceTypeFilterList}
                                        handleSelectedChange={handleServiceTypeFilterSelect}
                                        checkboxList={serviceTypeFilterList.sort()}
                                        checkboxWidth={160}
                                    />
                                    <FilterCheckBox
                                        title={'IAM'}
                                        selected={selectedIAMFilterList}
                                        handleSelectedChange={handleIAMFilterSelect}
                                        checkboxList={IAMFilterList.sort()}
                                        checkboxWidth={150}
                                    />
                                </div>
                                <div className='shopMonitorModalFooter'>
                                    <div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                verticalAlign: 'middle',
                                                height: 20,
                                                marginTop: 10,
                                            }}
                                        >
                                            <Checkbox
                                                value={showCompleted}
                                                checked={showCompleted}
                                                color='default'
                                                onChange={(
                                                    event: ChangeEvent<HTMLInputElement>
                                                ) => {
                                                    setAppliable(true)
                                                    setResetable(true)

                                                    setShowCompleted(event.target.checked)
                                                }}
                                                style={{
                                                    color: '#0062FF',
                                                    transform: 'scale(0.75)',
                                                }}
                                            />
                                            <div style={{ fontSize: 12, marginTop: 2 }}>
                                                Show Completed (YTD)
                                            </div>
                                        </div>

                                        <div
                                            style={{
                                                display: 'flex',
                                                verticalAlign: 'middle',
                                                height: 20,
                                            }}
                                        >
                                            <Checkbox
                                                value={showQuotes}
                                                checked={showQuotes}
                                                color='default'
                                                onChange={(
                                                    event: ChangeEvent<HTMLInputElement>
                                                ) => {
                                                    setAppliable(true)
                                                    setResetable(true)

                                                    setShowQuotes(event.target.checked)
                                                }}
                                                style={{
                                                    color: '#0062FF',
                                                    transform: 'scale(0.75)',
                                                }}
                                            />
                                            <div style={{ fontSize: 12, marginTop: 2 }}>
                                                Show Quotes
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ marginLeft: 25, marginTop: 15, display: 'flex' }}>
                                        <button
                                            className='cancelButton'
                                            onClick={() => closeModal(false)}
                                            style={{ width: 94 }}
                                        >
                                            Close
                                        </button>
                                        <button
                                            className={
                                                resetable ? 'cancelButton' : 'disabledButton'
                                            }
                                            onClick={
                                                resetable
                                                    ? () => {
                                                          resetModalFilters()
                                                          fetchShopLogData(false)
                                                      }
                                                    : () => {}
                                            }
                                            style={{ marginLeft: 50 }}
                                        >
                                            Clear Filters
                                        </button>
                                        <button
                                            className={appliable ? 'aButton' : 'disabledButton'}
                                            onClick={() => (appliable ? closeModal(true) : {})}
                                            style={{ marginLeft: 20, width: 94 }}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    )
}
